import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, QrCode, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { papi } from "@/lib/portalApi";
import { fmtDate, fmtMoney } from "@/lib/format";

export const decodeQr = async (text) => (await papi.post("/portal/qr/decode", { text })).data;

export const scanImageFiles = async (files) => {
  const el = document.getElementById("qr-file-scan") || Object.assign(document.createElement("div"), { id: "qr-file-scan", hidden: true });
  if (!el.isConnected) { document.body.appendChild(el); }
  const reader = new Html5Qrcode("qr-file-scan", { verbose: false });
  for (const f of files) {
    try { const text = await reader.scanFile(f, false); return { text, origin: "photo", file: f, ...(await decodeQr(text)) }; } catch { /* try next */ }
  }
  return null;
};

export const QrScanner = ({ onResult, onClose }) => {
  const [err, setErr] = useState("");
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const s = new Html5Qrcode("qr-live", { verbose: false });
    ref.current = s;
    s.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } }, async (text) => {
      if (done.current) { return; }
      done.current = true;
      try { const z = await decodeQr(text); await s.stop().catch(() => {}); onResult({ text, ...z }); } catch (e) { setErr(e?.response?.data?.detail || "Not a ZATCA e-invoice QR"); done.current = false; }
    }, () => {}).catch(() => setErr("Camera not available. Allow camera access, or upload a clear photo of the bill instead."));
    return () => { if (s.isScanning) { s.stop().catch(() => {}); } };
  }, [onResult]);
  return (
    <div className="fixed inset-0 z-[70] bg-black flex flex-col" data-testid="qr-scanner">
      <div className="flex items-center justify-between p-4 pt-[calc(1rem+env(safe-area-inset-top))] text-white">
        <div className="flex items-center gap-2 font-semibold"><QrCode className="w-5 h-5" />Scan invoice QR</div>
        <button onClick={onClose} className="p-1" data-testid="qr-scanner-close"><X className="w-6 h-6" /></button>
      </div>
      <div id="qr-live" className="flex-1 [&_video]:object-cover" />
      <div className="p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] text-center text-sm text-white/80">{err ? <span className="text-amber-300" data-testid="qr-scanner-error">{err}</span> : "Point the camera at the QR code printed on the invoice"}</div>
    </div>
  );
};

const R = ({ k, v, testid }) => <div className="flex justify-between gap-3 text-sm py-1"><span className="text-slate-500">{k}</span><span className="num text-right font-medium text-slate-900" data-testid={testid}>{v ?? "—"}</span></div>;

export const QrPreview = ({ qr, scanning, onClear }) => {
  if (scanning) return <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-2 text-sm text-slate-500" data-testid="qr-preview-scanning"><Loader2 className="w-4 h-4 animate-spin" />Looking for the e-invoice QR…</div>;
  if (qr === undefined) return null;
  if (!qr) {
    return <div className="bg-white rounded-2xl p-4 shadow-sm flex gap-2 text-sm text-slate-500" data-testid="qr-preview-none"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />No e-invoice QR found in the photo. You can still upload; details will be read from the photo.</div>;
  }
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-emerald-200" data-testid="qr-preview">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm"><CheckCircle2 className="w-4 h-4" />E-invoice QR read</div>
        {onClear && <button onClick={onClear} className="text-xs text-slate-400 underline" data-testid="qr-preview-clear">Remove</button>}
      </div>
      <R k="Vendor" v={qr.vendor_name} testid="qr-vendor" />
      <R k="VAT number" v={qr.vendor_vat_number} testid="qr-vat-no" />
      <R k="Date & time" v={qr.invoice_date ? `${fmtDate(qr.invoice_date)} ${qr.invoice_time ? fmtTime(qr.invoice_time) : ""}` : qr.timestamp} testid="qr-date" />
      <R k="Total (incl. VAT)" v={`SAR ${fmtMoney(qr.total)}`} testid="qr-total" />
      <R k="VAT amount" v={`SAR ${fmtMoney(qr.vat_amount)}`} testid="qr-vat" />
    </div>
  );
};

export const fmtTime = (hm) => {
  const [h, m] = hm.split(":").map(Number);
  return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
